import { Injectable } from '@angular/core';
import { II18nCountry } from './i-i18n-country';
import { i18nCountries } from './i18n-countries';
import camelCase from 'lodash/camelCase';
import find from 'lodash/find';

export declare let libphonenumber: any;

interface NormalizeResult {
  localNumber: string;
  normalized: string;
  updatedCountryIsoCode?: string;
}

@Injectable()
export class I18nCountryService {
  public allCountries: II18nCountry[];

  constructor() {
    this.allCountries = this._fetchCountryData();
  }

  public getCountryByIso2Code(iso2: string): II18nCountry | undefined {
    return find(this.allCountries, { iso2 });
  }

  public getCountryByDialCode(dialCode: string): II18nCountry | undefined {
    return find(this.allCountries, { dialCode: dialCode + '' });
  }

  public sortCountriesByPreferredIso2Codes(iso2Codes: string[]): II18nCountry[] {
    let filteredCountries: II18nCountry[] = this.allCountries;
    const preferredCountries: II18nCountry[] = [];
    iso2Codes.forEach((iso2) => {
      filteredCountries = filteredCountries.filter((c) => {
        if (c.iso2 === iso2) {
          preferredCountries.push(c);
          return false;
        }
        return true;
      });
    });
    return preferredCountries.concat(filteredCountries);
  }

  private _fetchCountryData(): II18nCountry[] {
    return i18nCountries.map<II18nCountry>((country) => {
      return {
        ...country,
        translate: 'common.countries.' + camelCase(country.name.trim().toLowerCase()),
        flagClass: 'flag flag-icon-background flag-icon-' + country.iso2.toLowerCase()
      };
    });
  }

  public parseNumber(number: string): { code: string, localNumber: string } {
    /*
    * Library libphonenumber-js loads in lib-phone-number.directive, web-utils
    * Uses CDN: https://cdn.cakeiteasy.com/libphonenumber.min.js
    * */
    const phoneNumber = libphonenumber.parsePhoneNumberFromString(number)
      || { countryCallingCode: '', nationalNumber: '' };

    const code = phoneNumber.countryCallingCode;
    const localNumber = phoneNumber.nationalNumber;

    return { localNumber, code };
  }

  public normalizePhoneNumber(input: string, country: II18nCountry): NormalizeResult {
    if (!input) {
      return { localNumber: '', normalized: '' };
    }
    const sanitized = this.sanitize(input);
    const fallBackResp = {
      localNumber: sanitized,
      normalized: country ? '+' + country.dialCode + sanitized : sanitized
    };

    if (!country) {
      return fallBackResp;
    }

    const parsed = libphonenumber.parsePhoneNumberFromString(sanitized, country.iso2);

    if (!parsed) {
      return fallBackResp;
    }

    return {
      localNumber: parsed.nationalNumber,
      normalized: parsed.number,
      ...(parsed.country !== country.iso2 ? { updatedCountryIsoCode: parsed.country } : {}),
    };
  }

  private sanitize(input: string): string {
    return input.replace(/[^\d+]/g, '');
  }
}
